import { useEffect, useRef, useState } from 'react';
import { toast } from '@strivelabs/ui-core';
import { useBlogs } from '@/hooks/useBlogs';
import type { BlogWithLatestActivity } from '@/services/DataService';
import { log } from '@/lib/logger';

/** How often the chat queue re-reads the blogs while it is on screen. */
const QUEUE_POLL_INTERVAL_MS = 15_000;

/** How many of the tenant's most recent blogs the chat queue shows. */
const QUEUE_PAGE_SIZE = 25;

/**
 * Action ids whose entries are being (or have been) submitted in this tab.
 * Guards against submitting the same chat request twice, e.g. when React
 * StrictMode mounts the slot twice in development, before the persisted
 * `entries_stored` flag has come back from the server.
 */
const submittedActionIds = new Set<string>();

/**
 * The result of submitting one chat entry.
 * - `claimed`: the blog was claimed and its run started.
 * - `skipped`: the entry was not usable (e.g. no keywords) and was not sent.
 * - `rejected`: the server would not start it (e.g. at capacity).
 */
export type SorQueueSubmitResult = 'claimed' | 'skipped' | 'rejected';

export interface UseSorBlogQueueConfig {
  /** Which half of the blog workflow this queue is for. */
  source: 'outline' | 'refresh';
  /** The entries the chat asked to queue. Nothing is submitted when empty. */
  entries?: unknown[];
  /** The chat action's own id, used to submit its entries only once. */
  actionId?: string;
  /** True when this chat request's entries were already submitted (persisted state). */
  alreadyStored: boolean;
  /** Called once every entry has been handled, so the caller can persist that. */
  onStoreSuccess: () => void;
  /** Starts one entry through the SOR blog APIs. */
  submitEntry: (entry: unknown) => Promise<SorQueueSubmitResult>;
  /** False while the agent is not installed for the tenant. */
  enabled: boolean;
}

export interface UseSorBlogQueueResult {
  /** The tenant's most recent blogs whose latest activity is this queue's source. */
  blogs: BlogWithLatestActivity[];
  isStoring: boolean;
  storeError: Error | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * The toast text for a finished submit, saying why anything did not start.
 *
 * @param source - Which queue this was.
 * @param total - How many entries the chat sent.
 * @param claimed - How many started.
 * @param skipped - How many were unusable (missing keywords or URL).
 * @param rejected - How many the server would not start (e.g. at capacity).
 *
 * @returns The toast description.
 */
export function describeSubmitResult (
  source: 'outline' | 'refresh',
  total: number,
  claimed: number,
  skipped: number,
  rejected: number
): string {
  if (skipped + rejected === 0) {
    return `Started ${claimed} ${claimed === 1 ? 'blog' : 'blogs'}. Track them in Blogs.`;
  }
  const reasons = [
    skipped > 0
      ? `${skipped} skipped (${source === 'refresh' ? 'missing or invalid URL' : 'no primary keyword'})`
      : '',
    rejected > 0 ? `${rejected} not started (too many already in progress, try again shortly)` : ''
  ].filter(Boolean);
  return `Started ${claimed} of ${total}. ${reasons.join('; ')}.`;
}

/**
 * Chat's blog queue on top of the SOR blog APIs: submits each chat entry
 * through the same endpoint /ui/blogs uses (claim, then start the run), then
 * polls the tenant's blogs for live status. Replaces the key_val TODO-bucket
 * queue, which SOR runs never update and the SOR refresher never drains.
 *
 * @param config - Queue configuration.
 *
 * @returns The blogs to show plus submit/fetch state.
 */
export function useSorBlogQueue ({
  source,
  entries,
  actionId,
  alreadyStored,
  onStoreSuccess,
  submitEntry,
  enabled
}: UseSorBlogQueueConfig): UseSorBlogQueueResult {
  const [isStoring, setIsStoring] = useState(false);
  const [storeError, setStoreError] = useState<Error | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const submitEntryRef = useRef(submitEntry);
  submitEntryRef.current = submitEntry;
  const onStoreSuccessRef = useRef(onStoreSuccess);
  onStoreSuccessRef.current = onStoreSuccess;

  const hasEntries = Array.isArray(entries) && entries.length > 0;
  const alreadySubmitted = alreadyStored || (actionId ? submittedActionIds.has(actionId) : false);

  useEffect(() => {
    // Read the set here, not at render time: StrictMode re-runs this effect
    // within the same render, after the first run has already claimed it.
    if (!enabled || !hasEntries || alreadySubmitted || (actionId && submittedActionIds.has(actionId))) {
      setSubmitted(true);
      return;
    }
    if (actionId) {
      submittedActionIds.add(actionId);
    }

    const submitAll = async () => {
      setIsStoring(true);
      setStoreError(null);
      let claimed = 0;
      let skipped = 0;
      let rejected = 0;
      try {
        // One at a time, in the order the chat listed them, the same way the
        // old queue started them.
        for (const entry of entries) {
          const result = await submitEntryRef.current(entry);
          if (result === 'claimed') {
            claimed += 1;
          } else if (result === 'skipped') {
            skipped += 1;
          } else {
            rejected += 1;
          }
        }

        log('info', `[chat ${source} queue] submitted`, { claimed, skipped, rejected });
        toast({
          title: claimed > 0 ? 'Started' : 'Nothing started',
          description: describeSubmitResult(source, entries.length, claimed, skipped, rejected),
          variant: skipped + rejected > 0 ? 'destructive' : 'success'
        });
        onStoreSuccessRef.current();
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error');
        log('error', `[chat ${source} queue] submit failed`, { error: err.message, claimed });
        setStoreError(err);
        toast({
          title: 'Error',
          description: `Started ${claimed} of ${entries.length}. The rest could not be started.`,
          variant: 'destructive'
        });
      } finally {
        setIsStoring(false);
        setSubmitted(true);
      }
    };

    void submitAll();
  }, [enabled, hasEntries, alreadySubmitted, actionId, entries, source]);

  const blogsQuery = useBlogs({ source: [ source ], page_size: QUEUE_PAGE_SIZE });
  const { refetch } = blogsQuery;

  useEffect(() => {
    if (!enabled || !submitted) {
      return undefined;
    }
    void refetch();
    const timer = setInterval(() => {
      void refetch();
    }, QUEUE_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [enabled, submitted, refetch]);

  return {
    blogs: blogsQuery.data?.blogs ?? [],
    isStoring,
    storeError,
    isLoading: !submitted || blogsQuery.isLoading,
    isError: blogsQuery.isError
  };
}
